import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/cn';

export const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-full font-medium border',
  {
    variants: {
      variant: {
        success:
          'bg-[var(--color-success-light)] text-[var(--color-success-dark)] border-transparent',
        warning:
          'bg-[var(--color-warning-light)] text-[var(--color-warning-dark)] border-transparent',
        danger:
          'bg-[var(--color-danger-light)] text-[var(--color-danger-dark)] border-transparent',
        info:
          'bg-[var(--color-info-light)] text-[var(--color-info-dark)] border-transparent',
        neutral:
          'bg-[var(--color-surface-2)] text-[var(--color-fg-secondary)] border-[var(--color-border)]',
        orange:
          'bg-[var(--color-accent-subtle)] text-[var(--color-accent)] border-transparent',
      },
      size: {
        sm: 'h-5 px-2 text-[10px]',
        md: 'h-6 px-2.5 text-xs',
      },
    },
    defaultVariants: { variant: 'neutral', size: 'md' },
  },
);

const dotColor = {
  success: 'bg-[var(--color-success)]',
  warning: 'bg-[var(--color-warning)]',
  danger: 'bg-[var(--color-danger)]',
  info: 'bg-[var(--color-info)]',
  neutral: 'bg-[var(--color-fg-muted)]',
  orange: 'bg-[var(--color-accent)]',
};

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  dot?: boolean;
}

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant = 'neutral', size, dot, children, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(badgeVariants({ variant, size }), className)}
      {...props}
    >
      {dot ? (
        <span
          className={cn(
            'inline-block h-1.5 w-1.5 rounded-full',
            dotColor[variant ?? 'neutral'],
          )}
        />
      ) : null}
      {children}
    </span>
  ),
);
Badge.displayName = 'Badge';
